// One-shot hook for fetching a single opportunity + its related list.
// Detail pages call this — the provider only caches the card list.

import { useEffect, useState, useCallback, useRef } from "react";
import { getOpportunityById } from "@/services";
import { normalizeDigitsDeep } from "@/utils/normalizeDigits";

const isAbortError = (e) =>
  e && (e.name === "AbortError" || String(e).includes("aborted"));

export const useOpportunity = (id) => {
  const [opportunity, setOpportunity] = useState(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  // Bumping this state forces the fetch effect to re-run on retry.
  const [reloadTick, setReloadTick]   = useState(0);
  const controllerRef                 = useRef(null);

  useEffect(() => {
    if (!id) {
      setOpportunity(null);
      setIsLoading(false);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsLoading(true);
    setError(null);

    getOpportunityById(id, { signal: controller.signal })
      .then((row) => {
        if (controller.signal.aborted) return;
        setOpportunity(normalizeDigitsDeep(row));
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [id, reloadTick]);

  const refresh = useCallback(() => setReloadTick((n) => n + 1), []);

  return { opportunity, isLoading, error, refresh };
};

/** Resolve related opportunity IDs against the currently loaded cards. */
export const resolveRelated = (relatedIds, allOpportunities) =>
  (relatedIds || [])
    .map((rid) => allOpportunities.find((o) => o.id === rid))
    .filter(Boolean);
